/**
 * apps/battle/client/src/terminal/components/BattleAnimation.tsx
 *
 * Phase 4.1: animated "battling…" frames that replace the static "> grading…"
 * line while the LLM grader is in flight. Each mark-tier (1, 2, 3, 6, 9, 12)
 * has its own frame sequence — higher-tier questions get longer, more
 * detailed sequences. No AO labels (per spec).
 *
 * Glyph choice: Unicode emoji + box-drawing only. NO multi-line ASCII art
 * (it doesn't render reliably across browsers/fonts).
 *
 * Behaviour:
 *   - Frames cycle on a per-tier interval.
 *   - If the grader is still working after the last frame, the loop restarts
 *     from frame 0. If the grader returns mid-loop, the parent simply
 *     unmounts this component → animation cuts cleanly to the verdict.
 *   - If the grader errored (`error` prop set), shows a single dimmed line
 *     ` × grader unavailable ` instead of the animation.
 */
import { useEffect, useState } from 'react';

export interface BattleAnimationProps {
  /** The question's mark value (1, 2, 3, 6, 9, or 12). Unknown tiers fall back to the 1-mark sequence. */
  marks: number;
  /** When truthy, replace the animation with a dimmed grader-error notice. */
  error?: string | null;
}

interface TierSpec {
  /** Display ms per frame. */
  interval: number;
  /** Frame sequence. Each frame is rendered verbatim inside <pre>. */
  frames: string[];
}

// All frames are width-padded with NB-spaces so consecutive frames have the
// same visual width (no horizontal jitter). The widest line determines
// container width.

// ---- 1-mark (3 frames, 200ms) — single slash ----
const TIER_1: TierSpec = {
  interval: 200,
  frames: [
    '─ ─ ⚔ ─ ─',
    '─ ─ ╳ ─ ─',
    '─ ─ ✦ ─ ─',
  ],
};

// ---- 2-mark (4 frames, 250ms) — sword swing arc ----
const TIER_2: TierSpec = {
  interval: 250,
  frames: [
    '─ ⚔ · · · · ·',
    '─ · ⚔ · · · ·',
    '─ · · ⚔ · · ·',
    '─ · · · ⚔ ✦ ★',
  ],
};

// ---- 3-mark (5 frames, 300ms) — quick duel exchange ----
const TIER_3: TierSpec = {
  interval: 300,
  frames: [
    '🗡️  · · · · · ·  🛡️',
    '🗡️  · · ⚔ · · ·  🛡️',
    '🗡️  · · ⚔⚔ · ·  🛡️',
    '🗡️  · ✨ ⚔ ✨ ·  🛡️',
    '🗡️  ★ ✦ HIT ✦ ★  🛡️',
  ],
};

// ---- 6-mark (6 frames, 400ms, 2 lines) — sustained duel + spark ----
const TIER_6: TierSpec = {
  interval: 400,
  frames: [
    '🗡️                              🛡️ \n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️  →                           🛡️ \n─ ─ ─ ⚔ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '    🗡️  ⚔                       🛡️ \n─ ─ ─ ─ 💥 ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '    🗡️  ⚔  ⚡                  🛡️ \n─ ─ ─ ─ ─ ─ ✨ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️                ✦      ✦  🛡️ \n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️         ★  ✦  RESOLVED  ✦  ★  🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
  ],
};

// ---- 9-mark (8 frames, 440ms, 2 lines) — multi-stage with block ----
const TIER_9: TierSpec = {
  interval: 440,
  frames: [
    '🗡️                                       🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️  ⚔  →                                 🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '        🗡️   ⚔   💥                      🛡️\n─ ─ ─ ─ ─ 💥 ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '        🗡️                  💥  🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ✨ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️  ←                                    🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️  →                       ←  ⚔   🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ⚔ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
    '🗡️  ✨                          ✨  🛡️\n─ ─ ─ ✦ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ✦ ─ ─ ─',
    '       ★  ✦   RESOLVED   ✦  ★\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─',
  ],
};

// ---- 12-mark (10 frames, 450ms, 3 lines) — epic boss-style sequence ----
const TIER_12: TierSpec = {
  interval: 450,
  frames: [
    '🗡️                                          🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         stance set',
    '🗡️  ⚔  →                                    🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         opening strike',
    '         🗡️   ⚔   💥                        🛡️\n─ ─ ─ ─ ─ 💥 ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         exchange',
    '         🗡️                      💥   🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ✨ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         block',
    '🗡️  ←                                       🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         recoil',
    '🗡️  →                       ←  ⚔   🛡️\n─ ─ ─ ─ ─ ─ ─ ─ ─ ⚔ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         counter',
    '🗡️  ⚡  →               ←  ⚡   🛡️\n─ ─ ─ ⚔ ─ ─ ─ ─ ─ ─ ─ ─ ─ ⚔ ─ ─ ─ ─ ─ ─\n         counter ii',
    '         💥  💥  💥  💥  💥  💥  💥\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         ULTIMATE',
    '       ✨  ⚡  ★  ✦  ★  ⚡  ✨\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         judgement rising',
    '   ⚡  ★  ✦  FINAL JUDGEMENT  ✦  ★  ⚡\n─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─\n         resolved',
  ],
};

function specForMarks(marks: number): TierSpec {
  switch (marks) {
    case 1: return TIER_1;
    case 2: return TIER_2;
    case 3: return TIER_3;
    case 6: return TIER_6;
    case 9: return TIER_9;
    case 12: return TIER_12;
    default:
      // Unknown tier: pick the closest by mark value.
      if (marks >= 10) return TIER_12;
      if (marks >= 7) return TIER_9;
      if (marks >= 4) return TIER_6;
      if (marks >= 3) return TIER_3;
      if (marks >= 2) return TIER_2;
      return TIER_1;
  }
}

export function BattleAnimation({ marks, error }: BattleAnimationProps) {
  const spec = specForMarks(marks);
  const [frameIdx, setFrameIdx] = useState(0);

  useEffect(() => {
    if (error) return;
    setFrameIdx(0);
    const id = window.setInterval(() => {
      setFrameIdx((i) => (i + 1) % spec.frames.length);
    }, spec.interval);
    return () => window.clearInterval(id);
  }, [spec, error]);

  if (error) {
    return (
      <div className="tm-battle-anim tm-battle-anim--error" role="status" aria-live="polite">
        × grader unavailable
      </div>
    );
  }

  return (
    <div className="tm-battle-anim" role="status" aria-live="polite" aria-label="Battling">
      <pre className="tm-battle-anim__frame">{spec.frames[frameIdx]}</pre>
      <div className="tm-battle-anim__label">{'>'} battling…</div>
    </div>
  );
}
